import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tooltip } from "../../common/components/Tooltip/Tooltip";
import i18n from "../../common/template/i18n";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import {
  type DateRange,
  EmptyState,
  SkeletonCard,
  useRankingTabOptions,
  CreateLearnerGroupModal,
  CreateParcoursGroupModal,
} from "../../features/statsNew/components";
import SegmentedControl from "src/common/components/SegmentedControl/SegmentedControl";
import {
  SessionsPerMonthComparisonChart,
  ActivityHeatmapComparison,
} from "../../features/statsNew/components/charts";
import { GazeHeatmapModal } from "../../features/statsNew/components/modals";
import type { RankingEntityType } from "../../features/statsNew/hooks";
import { useLearnerGroups, useAvailable360Scenes } from "../../features/statsNew/hooks";
import { useParcoursGroups } from "../../features/statsNew/hooks/useParcoursGroups";
import type { AvailableLearner, AvailableParcours } from "../../features/statsNew/types/groupTypes";
import { getSessionDate, isSessionInDateRange } from "../../features/statsNew/utils/dateUtils";
import { getEntityColor } from "../../features/statsNew/utils/comparisonColors";
import { shouldIncludeDurationInAverage } from "../../features/statsNew/utils/sessionValidation";
import {
  getAllSessionsByProjectGroup,
  sessionsByProjectGroupSelector,
} from "../../features/stats/state/statsSlice";

interface OutletContextType {
  dateRange: DateRange;
}

interface EntityData {
  id: number | string;
  name: string;
  totalSessions: number;
  completedSessions: number;
  completionRate: number;
  averageProgression: number | null;
  averageScore: number | null;
  averageScoreMax: number | null;
  averageDuration: number | null;
  lastSessionDate: Date | null;
  memberCount?: number; // Only for groups
}

const MAX_COMPARE = 3;

// Metric row component with tooltip
const MetricLabel = ({ label, tooltip }: { label: string; tooltip: string }) => (
  <div className="flex items-center gap-1.5">
    <span>{label}</span>
    <div className="group relative">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4 text-gray-400 cursor-help hover:text-gray-600 transition-colors"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200">
        <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 w-64 shadow-lg">
          {tooltip}
          <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
        </div>
      </div>
    </div>
  </div>
);

const ComparateurView = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { dateRange } = useOutletContext<OutletContextType>();
  const sessionsByProjectGroup = useAppSelector(sessionsByProjectGroupSelector);

  const [entityType, setEntityType] = useState<RankingEntityType>("parcours");
  const rankingTabOptions = useRankingTabOptions(true);
  const [selectedIds, setSelectedIds] = useState<Set<number | string>>(new Set());
  const [hasFetched, setHasFetched] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showCreateParcoursGroupModal, setShowCreateParcoursGroupModal] = useState(false);
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());
  const [showSceneSelector, setShowSceneSelector] = useState(false);
  const [showHeatmapComparison, setShowHeatmapComparison] = useState(false);
  const [selectedSceneForComparison, setSelectedSceneForComparison] = useState<{
    id: number;
    name: string;
  } | null>(null);

  // Learner groups hook
  const { groups, createGroup, deleteGroup, createSmartGroup, canCreateGroup } = useLearnerGroups();

  // Parcours groups hook
  const {
    groups: parcoursGroups,
    createGroup: createParcoursGroup,
    deleteGroup: deleteParcoursGroup,
    canCreateGroup: canCreateParcoursGroup,
  } = useParcoursGroups();

  // Get one session per parcours for fetching available 360° scenes
  const sessionsForSceneFetch = useMemo(() => {
    const sessions: { sessionId: number; parcoursId: number; parcoursName: string }[] = [];
    const seenParcoursIds = new Set<number>();

    for (const pg of sessionsByProjectGroup) {
      if (seenParcoursIds.has(pg.project_group_id)) continue;

      // Find the first session in this parcours
      for (const project of pg.projects) {
        if (project.sessions.length > 0) {
          sessions.push({
            sessionId: project.sessions[0].id,
            parcoursId: pg.project_group_id,
            parcoursName: pg.project_group_name,
          });
          seenParcoursIds.add(pg.project_group_id);
          break;
        }
      }
    }

    return sessions;
  }, [sessionsByProjectGroup]);

  // Available 360° scenes for heatmap comparison (only fetch when scene selector is open)
  const { scenes: available360Scenes, isLoading: isLoadingScenes } = useAvailable360Scenes(
    sessionsForSceneFetch,
    showSceneSelector,
  );

  // Fetch data on mount; gate the skeleton on fetch completion so empty orgs reach the empty state
  useEffect(() => {
    void dispatch(getAllSessionsByProjectGroup({ componentId: "statsNewComparateurView" })).finally(
      () => setHasFetched(true),
    );
  }, [dispatch]);

  // Reset selection when entity type changes (except for groups which use a different selection mechanism)
  useEffect(() => {
    if (entityType !== "groupes" && entityType !== "groupes_parcours") {
      setSelectedIds(new Set());
    }
  }, [entityType]);

  // Get available parcours for smart grouping (simple list for learner group modal)
  const availableParcours = useMemo(() => {
    return sessionsByProjectGroup.map((group) => ({
      id: group.project_group_id,
      name: group.project_group_name,
    }));
  }, [sessionsByProjectGroup]);

  // Get available parcours with stats for parcours group modal
  const availableParcoursWithStats = useMemo((): AvailableParcours[] => {
    return sessionsByProjectGroup.map((group) => {
      let totalSessions = 0;
      let completedSessions = 0;
      let totalScore = 0;
      let totalScoreMax = 0;
      let scoreCount = 0;
      let lastSessionDate: Date | null = null;

      group.projects.forEach((project) => {
        project.sessions.forEach((session) => {
          const sessionDate = getSessionDate(session);
          if (!isSessionInDateRange(sessionDate, dateRange)) return;

          totalSessions++;
          if (session.is_completed) completedSessions++;
          if (session.score !== null && session.score !== undefined) {
            totalScore += session.score;
            totalScoreMax += session.score_max ?? 0;
            scoreCount++;
          }
          if (!lastSessionDate || sessionDate > lastSessionDate) {
            lastSessionDate = sessionDate;
          }
        });
      });

      return {
        id: group.project_group_id,
        name: group.project_group_name,
        totalSessions,
        completionRate:
          totalSessions > 0 ? Math.round((completedSessions / totalSessions) * 100) : 0,
        averageScore: scoreCount > 0 ? Math.round((totalScore / scoreCount) * 10) / 10 : null,
        averageScoreMax: scoreCount > 0 ? Math.round(totalScoreMax / scoreCount) : null,
        lastSessionDate,
      };
    });
  }, [sessionsByProjectGroup, dateRange]);

  // Get all available learners (for the group creation modal)
  const availableLearners = useMemo((): AvailableLearner[] => {
    const userMap = new Map<
      string,
      {
        id: string;
        name: string;
        totalSessions: number;
        completedSessions: number;
        totalScore: number;
        totalScoreMax: number;
        scoreCount: number;
        lastSessionDate: Date | null;
      }
    >();

    sessionsByProjectGroup.forEach((group) => {
      group.projects.forEach((project) => {
        project.sessions.forEach((session) => {
          const sessionDate = getSessionDate(session);
          if (!isSessionInDateRange(sessionDate, dateRange)) return;

          let userId: string;
          let userName: string;

          if (session.enduser) {
            userId = `user-${session.enduser.id}`;
            userName =
              `${session.enduser.firstname || ""} ${session.enduser.lastname || ""}`.trim() ||
              session.enduser.username ||
              session.enduser.email ||
              "Anonyme";
          } else if (session.user_name) {
            userId = `name-${session.user_name}`;
            userName = session.user_name;
          } else {
            return;
          }

          const existing = userMap.get(userId);
          if (existing) {
            existing.totalSessions++;
            if (session.is_completed) existing.completedSessions++;
            if (session.score !== null && session.score !== undefined) {
              existing.totalScore += session.score;
              existing.totalScoreMax += session.score_max ?? 0;
              existing.scoreCount++;
            }
            if (!existing.lastSessionDate || sessionDate > existing.lastSessionDate) {
              existing.lastSessionDate = sessionDate;
            }
          } else {
            userMap.set(userId, {
              id: userId,
              name: userName,
              totalSessions: 1,
              completedSessions: session.is_completed ? 1 : 0,
              totalScore: session.score ?? 0,
              totalScoreMax: session.score_max ?? 0,
              scoreCount: session.score !== null && session.score !== undefined ? 1 : 0,
              lastSessionDate: sessionDate,
            });
          }
        });
      });
    });

    return Array.from(userMap.values()).map((user) => ({
      id: user.id,
      name: user.name,
      totalSessions: user.totalSessions,
      completionRate: Math.round((user.completedSessions / user.totalSessions) * 100),
      averageScore:
        user.scoreCount > 0 ? Math.round((user.totalScore / user.scoreCount) * 10) / 10 : null,
      averageScoreMax:
        user.scoreCount > 0 ? Math.round(user.totalScoreMax / user.scoreCount) : null,
      lastSessionDate: user.lastSessionDate,
    }));
  }, [sessionsByProjectGroup, dateRange]);

  // Get all available entities
  const availableEntities = useMemo((): EntityData[] => {
    const entities: EntityData[] = [];

    if (entityType === "parcours") {
      sessionsByProjectGroup.forEach((group) => {
        let totalSessions = 0;
        let completedSessions = 0;
        let totalScore = 0;
        let totalScoreMax = 0;
        let scoreCount = 0;
        let totalDuration = 0;
        let durationCount = 0;
        let totalProgression = 0;
        let progressionCount = 0;
        let lastSessionDate: Date | null = null;

        group.projects.forEach((project) => {
          project.sessions.forEach((session) => {
            const sessionDate = getSessionDate(session);
            if (!isSessionInDateRange(sessionDate, dateRange)) return;

            totalSessions++;
            if (session.is_completed) completedSessions++;
            if (session.score !== null && session.score !== undefined) {
              totalScore += session.score;
              totalScoreMax += session.score_max ?? 0;
              scoreCount++;
            }
            // Only include duration if it passes the validation (excludes old sessions < 30s)
            const createdAt = session.createdAt || session.created_at_ts;
            if (
              session.total_session_duration &&
              createdAt &&
              shouldIncludeDurationInAverage(createdAt, session.total_session_duration)
            ) {
              totalDuration += session.total_session_duration;
              durationCount++;
            }
            // Calculate average progression
            if (session.total_session_completion_percentage != null) {
              totalProgression += session.total_session_completion_percentage;
              progressionCount++;
            }
            if (!lastSessionDate || sessionDate > lastSessionDate) {
              lastSessionDate = sessionDate;
            }
          });
        });

        if (totalSessions > 0) {
          entities.push({
            id: group.project_group_id,
            name: group.project_group_name,
            totalSessions,
            completedSessions,
            completionRate: Math.round((completedSessions / totalSessions) * 100),
            averageProgression:
              progressionCount > 0 ? Math.round(totalProgression / progressionCount) : null,
            averageScore: scoreCount > 0 ? Math.round((totalScore / scoreCount) * 10) / 10 : null,
            averageScoreMax: scoreCount > 0 ? Math.round(totalScoreMax / scoreCount) : null,
            averageDuration: durationCount > 0 ? Math.round(totalDuration / durationCount) : null,
            lastSessionDate,
          });
        }
      });
    } else if (entityType === "modules") {
      sessionsByProjectGroup.forEach((group) => {
        group.projects.forEach((project) => {
          let totalSessions = 0;
          let completedSessions = 0;
          let totalScore = 0;
          let totalScoreMax = 0;
          let scoreCount = 0;
          let totalDuration = 0;
          let durationCount = 0;
          let totalProgression = 0;
          let progressionCount = 0;
          let lastSessionDate: Date | null = null;

          project.sessions.forEach((session) => {
            const sessionDate = getSessionDate(session);
            if (!isSessionInDateRange(sessionDate, dateRange)) return;

            totalSessions++;
            if (session.is_completed) completedSessions++;
            if (session.score !== null && session.score !== undefined) {
              totalScore += session.score;
              totalScoreMax += session.score_max ?? 0;
              scoreCount++;
            }
            // Only include duration if it passes the validation (excludes old sessions < 30s)
            const createdAt = session.createdAt || session.created_at_ts;
            if (
              session.total_session_duration &&
              createdAt &&
              shouldIncludeDurationInAverage(createdAt, session.total_session_duration)
            ) {
              totalDuration += session.total_session_duration;
              durationCount++;
            }
            // Calculate average progression
            if (session.total_session_completion_percentage != null) {
              totalProgression += session.total_session_completion_percentage;
              progressionCount++;
            }
            if (!lastSessionDate || sessionDate > lastSessionDate) {
              lastSessionDate = sessionDate;
            }
          });

          if (totalSessions > 0) {
            entities.push({
              id: project.project_id,
              name: project.project_name || `Module ${project.project_id}`,
              totalSessions,
              completedSessions,
              completionRate: Math.round((completedSessions / totalSessions) * 100),
              averageProgression:
                progressionCount > 0 ? Math.round(totalProgression / progressionCount) : null,
              averageScore: scoreCount > 0 ? Math.round((totalScore / scoreCount) * 10) / 10 : null,
              averageScoreMax: scoreCount > 0 ? Math.round(totalScoreMax / scoreCount) : null,
              averageDuration: durationCount > 0 ? Math.round(totalDuration / durationCount) : null,
              lastSessionDate,
            });
          }
        });
      });
    } else if (entityType === "apprenants") {
      const userMap = new Map<
        string,
        EntityData & {
          scoreCount: number;
          durationCount: number;
          progressionCount: number;
          totalScoreMax: number;
        }
      >();

      sessionsByProjectGroup.forEach((group) => {
        group.projects.forEach((project) => {
          project.sessions.forEach((session) => {
            const sessionDate = getSessionDate(session);
            if (!isSessionInDateRange(sessionDate, dateRange)) return;

            let userId: string;
            let userName: string;

            if (session.enduser) {
              userId = `user-${session.enduser.id}`;
              userName =
                `${session.enduser.firstname || ""} ${session.enduser.lastname || ""}`.trim() ||
                session.enduser.username ||
                session.enduser.email ||
                "Anonyme";
            } else if (session.user_name) {
              userId = `name-${session.user_name}`;
              userName = session.user_name;
            } else {
              return;
            }

            // Check if duration should be included in average
            const createdAt = session.createdAt || session.created_at_ts;
            const includeDuration =
              session.total_session_duration &&
              createdAt &&
              shouldIncludeDurationInAverage(createdAt, session.total_session_duration);

            const existing = userMap.get(userId);
            if (existing) {
              existing.totalSessions++;
              if (session.is_completed) existing.completedSessions++;
              if (session.score !== null && session.score !== undefined) {
                existing.averageScore = (existing.averageScore || 0) + session.score;
                existing.totalScoreMax += session.score_max ?? 0;
                existing.scoreCount++;
              }
              // Only include duration if it passes the validation (excludes old sessions < 30s)
              if (includeDuration && session.total_session_duration != null) {
                existing.averageDuration =
                  (existing.averageDuration || 0) + session.total_session_duration;
                existing.durationCount++;
              }
              // Track progression
              if (session.total_session_completion_percentage != null) {
                existing.averageProgression =
                  (existing.averageProgression || 0) + session.total_session_completion_percentage;
                existing.progressionCount++;
              }
              if (!existing.lastSessionDate || sessionDate > existing.lastSessionDate) {
                existing.lastSessionDate = sessionDate;
              }
            } else {
              userMap.set(userId, {
                id: userId,
                name: userName,
                totalSessions: 1,
                completedSessions: session.is_completed ? 1 : 0,
                completionRate: 0,
                averageProgression: session.total_session_completion_percentage ?? null,
                averageScore: session.score ?? null,
                averageScoreMax: null,
                totalScoreMax: session.score_max ?? 0,
                averageDuration: includeDuration ? session.total_session_duration ?? null : null,
                lastSessionDate: sessionDate,
                scoreCount: session.score !== null && session.score !== undefined ? 1 : 0,
                durationCount: includeDuration ? 1 : 0,
                progressionCount: session.total_session_completion_percentage != null ? 1 : 0,
              });
            }
          });
        });
      });

      userMap.forEach((user) => {
        entities.push({
          id: user.id,
          name: user.name,
          totalSessions: user.totalSessions,
          completedSessions: user.completedSessions,
          completionRate: Math.round((user.completedSessions / user.totalSessions) * 100),
          averageProgression:
            user.progressionCount > 0
              ? Math.round((user.averageProgression || 0) / user.progressionCount)
              : null,
          averageScore:
            user.scoreCount > 0
              ? Math.round(((user.averageScore || 0) / user.scoreCount) * 10) / 10
              : null,
          averageScoreMax:
            user.scoreCount > 0 ? Math.round(user.totalScoreMax / user.scoreCount) : null,
          averageDuration:
            user.durationCount > 0
              ? Math.round((user.averageDuration || 0) / user.durationCount)
              : null,
          lastSessionDate: user.lastSessionDate,
        });
      });
    } else if (entityType === "groupes") {
      // Calculate stats for each group
      groups.forEach((group) => {
        let totalSessions = 0;
        let completedSessions = 0;
        let totalScore = 0;
        let totalScoreMax = 0;
        let scoreCount = 0;
        let totalDuration = 0;
        let durationCount = 0;
        let totalProgression = 0;
        let progressionCount = 0;
        let lastSessionDate: Date | null = null;

        sessionsByProjectGroup.forEach((pg) => {
          pg.projects.forEach((project) => {
            project.sessions.forEach((session) => {
              // Check if the session belongs to a member of this group
              const memberId = session.enduser
                ? `user-${session.enduser.id}`
                : session.user_name
                ? `name-${session.user_name}`
                : null;

              if (!memberId || !group.memberIds.includes(memberId)) return;

              const sessionDate = getSessionDate(session);
              if (!isSessionInDateRange(sessionDate, dateRange)) return;

              totalSessions++;
              if (session.is_completed) completedSessions++;
              if (session.score !== null && session.score !== undefined) {
                totalScore += session.score;
                totalScoreMax += session.score_max ?? 0;
                scoreCount++;
              }
              const createdAt = session.createdAt || session.created_at_ts;
              if (
                session.total_session_duration &&
                createdAt &&
                shouldIncludeDurationInAverage(createdAt, session.total_session_duration)
              ) {
                totalDuration += session.total_session_duration;
                durationCount++;
              }
              // Calculate average progression
              if (session.total_session_completion_percentage != null) {
                totalProgression += session.total_session_completion_percentage;
                progressionCount++;
              }
              if (!lastSessionDate || sessionDate > lastSessionDate) {
                lastSessionDate = sessionDate;
              }
            });
          });
        });

        entities.push({
          id: group.id,
          name: group.name,
          totalSessions,
          completedSessions,
          completionRate:
            totalSessions > 0 ? Math.round((completedSessions / totalSessions) * 100) : 0,
          averageProgression:
            progressionCount > 0 ? Math.round(totalProgression / progressionCount) : null,
          averageScore: scoreCount > 0 ? Math.round((totalScore / scoreCount) * 10) / 10 : null,
          averageScoreMax: scoreCount > 0 ? Math.round(totalScoreMax / scoreCount) : null,
          averageDuration: durationCount > 0 ? Math.round(totalDuration / durationCount) : null,
          lastSessionDate,
          memberCount: group.memberIds.length,
        });
      });
    } else if (entityType === "groupes_parcours") {
      // Calculate stats for each parcours group
      parcoursGroups.forEach((group) => {
        let totalSessions = 0;
        let completedSessions = 0;
        let totalScore = 0;
        let totalScoreMax = 0;
        let scoreCount = 0;
        let totalDuration = 0;
        let durationCount = 0;
        let totalProgression = 0;
        let progressionCount = 0;
        let lastSessionDate: Date | null = null;

        sessionsByProjectGroup.forEach((pg) => {
          // Check if this project group is part of the parcours group
          if (!group.parcoursIds.includes(pg.project_group_id)) return;

          pg.projects.forEach((project) => {
            project.sessions.forEach((session) => {
              const sessionDate = getSessionDate(session);
              if (!isSessionInDateRange(sessionDate, dateRange)) return;

              totalSessions++;
              if (session.is_completed) completedSessions++;
              if (session.score !== null && session.score !== undefined) {
                totalScore += session.score;
                totalScoreMax += session.score_max ?? 0;
                scoreCount++;
              }
              const createdAt = session.createdAt || session.created_at_ts;
              if (
                session.total_session_duration &&
                createdAt &&
                shouldIncludeDurationInAverage(createdAt, session.total_session_duration)
              ) {
                totalDuration += session.total_session_duration;
                durationCount++;
              }
              // Calculate average progression
              if (session.total_session_completion_percentage != null) {
                totalProgression += session.total_session_completion_percentage;
                progressionCount++;
              }
              if (!lastSessionDate || sessionDate > lastSessionDate) {
                lastSessionDate = sessionDate;
              }
            });
          });
        });

        entities.push({
          id: group.id,
          name: group.name,
          totalSessions,
          completedSessions,
          completionRate:
            totalSessions > 0 ? Math.round((completedSessions / totalSessions) * 100) : 0,
          averageProgression:
            progressionCount > 0 ? Math.round(totalProgression / progressionCount) : null,
          averageScore: scoreCount > 0 ? Math.round((totalScore / scoreCount) * 10) / 10 : null,
          averageScoreMax: scoreCount > 0 ? Math.round(totalScoreMax / scoreCount) : null,
          averageDuration: durationCount > 0 ? Math.round(totalDuration / durationCount) : null,
          lastSessionDate,
          memberCount: group.parcoursIds.length,
        });
      });
    }

    return entities.sort((a, b) => b.totalSessions - a.totalSessions);
  }, [sessionsByProjectGroup, dateRange, entityType, groups, parcoursGroups]);

  // Filter by search
  const filteredEntities = useMemo(() => {
    if (!searchValue) return availableEntities;
    const search = searchValue.toLowerCase();
    return availableEntities.filter((e) => e.name.toLowerCase().includes(search));
  }, [availableEntities, searchValue]);

  // Selected entities data
  const selectedEntities = useMemo(() => {
    // For groups (learner or parcours), all created groups are automatically "selected" for comparison
    if (entityType === "groupes" || entityType === "groupes_parcours") {
      return availableEntities;
    }
    return availableEntities.filter((e) => selectedIds.has(e.id));
  }, [availableEntities, selectedIds, entityType]);

  // Extract sessions data per entity for charts
  const entitySessionsData = useMemo(() => {
    return selectedEntities.map((entity, index) => {
      const sessions: { date: Date }[] = [];
      const color = getEntityColor(index);

      if (entityType === "parcours") {
        const group = sessionsByProjectGroup.find((g) => g.project_group_id === entity.id);
        group?.projects.forEach((project) => {
          project.sessions.forEach((session) => {
            const sessionDate = getSessionDate(session);
            if (!isSessionInDateRange(sessionDate, dateRange)) return;
            sessions.push({ date: sessionDate });
          });
        });
      } else if (entityType === "modules") {
        // Search through all groups to find the project
        sessionsByProjectGroup.forEach((group) => {
          const project = group.projects.find((p) => p.project_id === entity.id);
          project?.sessions.forEach((session) => {
            const sessionDate = getSessionDate(session);
            if (!isSessionInDateRange(sessionDate, dateRange)) return;
            sessions.push({ date: sessionDate });
          });
        });
      } else if (entityType === "apprenants") {
        // Search through all sessions and filter by user ID
        sessionsByProjectGroup.forEach((group) => {
          group.projects.forEach((project) => {
            project.sessions.forEach((session) => {
              const userId = session.enduser
                ? `user-${session.enduser.id}`
                : session.user_name
                ? `name-${session.user_name}`
                : null;
              if (userId !== entity.id) return;

              const sessionDate = getSessionDate(session);
              if (!isSessionInDateRange(sessionDate, dateRange)) return;
              sessions.push({ date: sessionDate });
            });
          });
        });
      } else if (entityType === "groupes") {
        // Find the group and collect sessions from all its members
        const group = groups.find((g) => g.id === entity.id);
        if (group) {
          sessionsByProjectGroup.forEach((pg) => {
            pg.projects.forEach((project) => {
              project.sessions.forEach((session) => {
                const memberId = session.enduser
                  ? `user-${session.enduser.id}`
                  : session.user_name
                  ? `name-${session.user_name}`
                  : null;

                if (!memberId || !group.memberIds.includes(memberId)) return;

                const sessionDate = getSessionDate(session);
                if (!isSessionInDateRange(sessionDate, dateRange)) return;
                sessions.push({ date: sessionDate });
              });
            });
          });
        }
      } else if (entityType === "groupes_parcours") {
        // Find the parcours group and collect sessions from all its parcours
        const group = parcoursGroups.find((g) => g.id === entity.id);
        if (group) {
          sessionsByProjectGroup.forEach((pg) => {
            // Check if this project group is part of the parcours group
            if (!group.parcoursIds.includes(pg.project_group_id)) return;

            pg.projects.forEach((project) => {
              project.sessions.forEach((session) => {
                const sessionDate = getSessionDate(session);
                if (!isSessionInDateRange(sessionDate, dateRange)) return;
                sessions.push({ date: sessionDate });
              });
            });
          });
        }
      }

      return {
        entityId: entity.id,
        entityName: entity.name,
        color: color.primary,
        colorGradient: color.gradient,
        primaryColor: color.primary,
        sessions,
      };
    });
  }, [selectedEntities, sessionsByProjectGroup, entityType, dateRange, groups, parcoursGroups]);

  const handleToggleSelect = (id: number | string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else if (newSet.size < MAX_COMPARE) {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const formatDuration = (seconds: number | null): string => {
    if (seconds === null) return "-";
    const minutes = Math.round(seconds / 60);
    if (minutes < 1) return "< 1 min";
    return `${minutes} min`;
  };

  const formatLastSessionDate = (date: Date | null): string => {
    if (!date) return "-";
    const locale = i18n.language?.startsWith("fr") ? fr : enUS;
    return format(date, "dd MMM yyyy", { locale });
  };

  // Find best/worst for highlighting
  const getBestWorst = (metric: keyof EntityData) => {
    if (selectedEntities.length < 2) return { best: null, worst: null };

    const values = selectedEntities
      .map((e) => ({ id: e.id, value: e[metric] as number | null }))
      .filter((v) => v.value !== null);

    if (values.length < 2) return { best: null, worst: null };

    const sorted = [...values].sort((a, b) => (b.value || 0) - (a.value || 0));
    return { best: sorted[0]?.id, worst: sorted[sorted.length - 1]?.id };
  };

  // Show skeleton during initial load
  if (!hasFetched) {
    return (
      <div className="flex flex-col h-full">
        <div className="h-10 w-64 bg-gray-200 rounded animate-pulse mb-4" />
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} variant="kpi" />
          ))}
        </div>
      </div>
    );
  }

  // For groups (learner or parcours), we show an empty state only if there are no items to create groups from
  // For other entity types, we show empty state if no entities exist
  if (
    entityType !== "groupes" &&
    entityType !== "groupes_parcours" &&
    availableEntities.length === 0
  ) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-shrink-0 mb-4">
          <SegmentedControl<RankingEntityType>
            options={rankingTabOptions}
            value={entityType}
            onChange={setEntityType}
            size="lg"
          />
        </div>
        <EmptyState
          icon="folder"
          title={t("statsNew.emptyState.noPublishedParcours")}
          description={t("statsNew.emptyState.noPublishedParcoursDescription")}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Sub-tabs */}
      <div className="flex-shrink-0 mb-4">
        <SegmentedControl<RankingEntityType>
          options={rankingTabOptions}
          value={entityType}
          onChange={setEntityType}
          size="lg"
        />
      </div>

      <div className="flex gap-4 flex-1 min-h-0">
        {/* Left panel - Entity selection or Group management */}
        <div className="w-64 flex-shrink-0 flex flex-col bg-white rounded-xl border border-gray-200 overflow-hidden">
          {entityType === "groupes" ? (
            <>
              {/* Learner Groups panel header */}
              <div className="p-3 border-b border-gray-200">
                <button
                  onClick={() => setShowCreateGroupModal(true)}
                  disabled={!canCreateGroup}
                  className={`w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    canCreateGroup
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  + {t("statsNew.comparateur.groups.createGroup")}
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  {groups.length}/{MAX_COMPARE} {t("statsNew.comparateur.groups.groupsCount")}
                </p>

                {/* Compare Heatmaps button - hidden for now, will be enabled later */}
                {/* TODO: Re-enable when heatmap comparison feature is ready
                {groups.length >= 2 && (
                  <button
                    onClick={() => setShowSceneSelector(true)}
                    className="w-full mt-2 px-4 py-2 text-sm font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors flex items-center justify-center gap-2"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                    {t("statsNew.comparateur.heatmap.compareHeatmaps")}
                  </button>
                )}
                */}
              </div>

              {/* Learner Groups list */}
              <div className="flex-1 overflow-y-auto p-2">
                {groups.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center p-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-10 w-10 text-gray-300 mb-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                    <p className="text-sm text-gray-500">
                      {t("statsNew.comparateur.groups.noGroups")}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {t("statsNew.comparateur.groups.createGroupHint")}
                    </p>
                  </div>
                ) : (
                  groups.map((group, index) => {
                    const isExpanded = expandedGroupIds.has(group.id);
                    return (
                      <div
                        key={group.id}
                        className="mb-2 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden"
                      >
                        <div className="flex items-center p-2">
                          <button
                            onClick={() => {
                              const newSet = new Set(expandedGroupIds);
                              if (isExpanded) {
                                newSet.delete(group.id);
                              } else {
                                newSet.add(group.id);
                              }
                              setExpandedGroupIds(newSet);
                            }}
                            className="mr-1 text-gray-400 hover:text-gray-600"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={`h-4 w-4 transition-transform ${
                                isExpanded ? "rotate-90" : ""
                              }`}
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                          <div
                            className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                            style={{ backgroundColor: getEntityColor(index).primary }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {group.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {group.memberIds.length} {t("statsNew.comparateur.groups.members")}
                            </p>
                          </div>
                          <Tooltip
                            message={t("general.delete")}
                            className="flex-shrink-0"
                            classNameChildren="right-0"
                          >
                            <button
                              onClick={() => deleteGroup(group.id)}
                              className="text-gray-400 hover:text-red-500 p-1"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </button>
                          </Tooltip>
                        </div>
                        {isExpanded && (
                          <div className="px-3 pb-2 pt-1 border-t border-gray-200 bg-white">
                            {group.memberIds.map((memberId) => {
                              const learner = availableLearners.find((l) => l.id === memberId);
                              return (
                                <p
                                  key={String(memberId)}
                                  className="text-xs text-gray-600 py-0.5 truncate"
                                >
                                  {learner?.name || String(memberId)}
                                </p>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </>
          ) : entityType === "groupes_parcours" ? (
            <>
              {/* Parcours Groups panel header */}
              <div className="p-3 border-b border-gray-200">
                <button
                  onClick={() => setShowCreateParcoursGroupModal(true)}
                  disabled={!canCreateParcoursGroup}
                  className={`w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    canCreateParcoursGroup
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  + {t("statsNew.comparateur.parcoursGroups.createGroup")}
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  {parcoursGroups.length}/{MAX_COMPARE}{" "}
                  {t("statsNew.comparateur.parcoursGroups.groupsCount")}
                </p>
              </div>

              {/* Parcours Groups list */}
              <div className="flex-1 overflow-y-auto p-2">
                {parcoursGroups.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center p-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-10 w-10 text-gray-300 mb-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                    <p className="text-sm text-gray-500">
                      {t("statsNew.comparateur.parcoursGroups.noGroups")}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {t("statsNew.comparateur.parcoursGroups.createGroupHint")}
                    </p>
                  </div>
                ) : (
                  parcoursGroups.map((group, index) => {
                    const isExpanded = expandedGroupIds.has(group.id);
                    return (
                      <div
                        key={group.id}
                        className="mb-2 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden"
                      >
                        <div className="flex items-center p-2">
                          <button
                            onClick={() => {
                              const newSet = new Set(expandedGroupIds);
                              if (isExpanded) {
                                newSet.delete(group.id);
                              } else {
                                newSet.add(group.id);
                              }
                              setExpandedGroupIds(newSet);
                            }}
                            className="mr-1 text-gray-400 hover:text-gray-600"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={`h-4 w-4 transition-transform ${
                                isExpanded ? "rotate-90" : ""
                              }`}
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                          <div
                            className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                            style={{ backgroundColor: getEntityColor(index).primary }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {group.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {group.parcoursIds.length}{" "}
                              {t("statsNew.comparateur.parcoursGroups.members")}
                            </p>
                          </div>
                          <Tooltip
                            message={t("general.delete")}
                            className="flex-shrink-0"
                            classNameChildren="right-0"
                          >
                            <button
                              onClick={() => deleteParcoursGroup(group.id)}
                              className="text-gray-400 hover:text-red-500 p-1"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </button>
                          </Tooltip>
                        </div>
                        {isExpanded && (
                          <div className="px-3 pb-2 pt-1 border-t border-gray-200 bg-white">
                            {group.parcoursIds.map((parcoursId) => {
                              const parcours = availableParcoursWithStats.find(
                                (p) => p.id === parcoursId,
                              );
                              return (
                                <p
                                  key={parcoursId}
                                  className="text-xs text-gray-600 py-0.5 truncate"
                                >
                                  {parcours?.name || `Parcours ${parcoursId}`}
                                </p>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <>
              {/* Standard entity selection panel */}
              <div className="p-3 border-b border-gray-200">
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder={t("statsNew.comparateur.searchPlaceholder")}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-gray-500">
                    {t("statsNew.comparateur.selectHint", { max: MAX_COMPARE })}
                  </p>
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      selectedIds.size === MAX_COMPARE
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {selectedIds.size}/{MAX_COMPARE}
                  </span>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-2">
                {filteredEntities.map((entity) => {
                  const isSelected = selectedIds.has(entity.id);
                  const isDisabled = !isSelected && selectedIds.size >= MAX_COMPARE;

                  return (
                    <button
                      key={entity.id}
                      onClick={() => handleToggleSelect(entity.id)}
                      disabled={isDisabled}
                      className={`w-full text-left p-2 rounded-lg mb-1 transition-colors ${
                        isSelected
                          ? "bg-green-50 border-2 border-green-500"
                          : isDisabled
                          ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                          : "hover:bg-gray-50 border-2 border-transparent"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium truncate">{entity.name}</span>
                        {isSelected && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-green-500 flex-shrink-0"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">{entity.totalSessions} sessions</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Right panel - Content area */}
        <div className="flex-1 min-w-0 overflow-y-auto">
          {selectedEntities.length === 0 ? (
            <div className="flex items-center justify-center h-64 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
              <div className="text-center p-8">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12 text-gray-400 mx-auto mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {entityType === "groupes" ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  ) : entityType === "groupes_parcours" ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  )}
                </svg>
                <p className="text-gray-600 font-medium">
                  {entityType === "groupes"
                    ? t("statsNew.comparateur.groups.createToCompare")
                    : entityType === "groupes_parcours"
                    ? t("statsNew.comparateur.parcoursGroups.createToCompare")
                    : t("statsNew.comparateur.selectToCompare")}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {entityType === "groupes"
                    ? t("statsNew.comparateur.groups.createGroupHint")
                    : entityType === "groupes_parcours"
                    ? t("statsNew.comparateur.parcoursGroups.createGroupHint")
                    : t("statsNew.comparateur.selectHint", { max: MAX_COMPARE })}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Comparison table */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table
                  className="w-full table-fixed"
                  role="table"
                  aria-label={t("statsNew.comparateur.tableLabel")}
                >
                  <thead className="bg-gray-50">
                    <tr role="row">
                      <th
                        className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-32"
                        scope="col"
                      >
                        {t("statsNew.comparateur.metric")}
                      </th>
                      {selectedEntities.map((entity, index) => (
                        <th
                          key={entity.id}
                          className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-40"
                          scope="col"
                        >
                          <div className="flex flex-col items-center gap-0.5">
                            <div className="flex items-center gap-1">
                              <div
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={{ backgroundColor: getEntityColor(index).primary }}
                              />
                              <span className="truncate max-w-[100px]">{entity.name}</span>
                              <button
                                onClick={() =>
                                  entityType === "groupes"
                                    ? deleteGroup(entity.id as string)
                                    : entityType === "groupes_parcours"
                                    ? deleteParcoursGroup(entity.id as string)
                                    : handleToggleSelect(entity.id)
                                }
                                className="text-gray-400 hover:text-red-500 flex-shrink-0"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-3.5 w-3.5"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </button>
                            </div>
                            {entityType === "groupes" && entity.memberCount !== undefined && (
                              <span className="text-[10px] text-gray-400 font-normal">
                                {entity.memberCount} {t("statsNew.comparateur.groups.members")}
                              </span>
                            )}
                            {entityType === "groupes_parcours" && entity.memberCount !== undefined && (
                              <span className="text-[10px] text-gray-400 font-normal">
                                {entity.memberCount}{" "}
                                {t("statsNew.comparateur.parcoursGroups.members")}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {/* Sessions */}
                    <tr>
                      <td className="px-3 py-2 text-sm font-medium text-gray-700">
                        <MetricLabel
                          label={t("statsNew.comparateur.metrics.sessions")}
                          tooltip={t("statsNew.comparateur.metricsTooltips.sessions")}
                        />
                      </td>
                      {selectedEntities.map((entity) => {
                        const { best, worst } = getBestWorst("totalSessions");
                        return (
                          <td
                            key={entity.id}
                            className={`px-3 py-2 text-center text-base font-semibold ${
                              entity.id === best
                                ? "text-green-600 bg-green-50"
                                : entity.id === worst
                                ? "text-red-600 bg-red-50"
                                : "text-gray-900"
                            }`}
                          >
                            {entity.totalSessions}
                          </td>
                        );
                      })}
                    </tr>

                    {/* Completed */}
                    <tr>
                      <td className="px-3 py-2 text-sm font-medium text-gray-700">
                        <MetricLabel
                          label={t("statsNew.comparateur.metrics.completed")}
                          tooltip={t("statsNew.comparateur.metricsTooltips.completed")}
                        />
                      </td>
                      {selectedEntities.map((entity) => {
                        const { best, worst } = getBestWorst("completedSessions");
                        return (
                          <td
                            key={entity.id}
                            className={`px-3 py-2 text-center text-base font-semibold ${
                              entity.id === best
                                ? "text-green-600 bg-green-50"
                                : entity.id === worst
                                ? "text-red-600 bg-red-50"
                                : "text-gray-900"
                            }`}
                          >
                            {entity.completedSessions}
                          </td>
                        );
                      })}
                    </tr>

                    {/* Completion Rate */}
                    <tr>
                      <td className="px-3 py-2 text-sm font-medium text-gray-700">
                        <MetricLabel
                          label={t("statsNew.comparateur.metrics.completionRate")}
                          tooltip={t("statsNew.comparateur.metricsTooltips.completionRate")}
                        />
                      </td>
                      {selectedEntities.map((entity) => {
                        const { best, worst } = getBestWorst("completionRate");
                        return (
                          <td
                            key={entity.id}
                            className={`px-3 py-2 text-center ${
                              entity.id === best
                                ? "bg-green-50"
                                : entity.id === worst
                                ? "bg-red-50"
                                : ""
                            }`}
                          >
                            <div className="flex flex-col items-center">
                              <span
                                className={`text-base font-semibold ${
                                  entity.id === best
                                    ? "text-green-600"
                                    : entity.id === worst
                                    ? "text-red-600"
                                    : "text-gray-900"
                                }`}
                              >
                                {entity.completionRate}%
                              </span>
                              <div className="w-16 h-1.5 bg-gray-200 rounded-full mt-1">
                                <div
                                  className={`h-full rounded-full ${
                                    entity.id === best
                                      ? "bg-green-500"
                                      : entity.id === worst
                                      ? "bg-red-500"
                                      : "bg-gray-400"
                                  }`}
                                  style={{ width: `${entity.completionRate}%` }}
                                />
                              </div>
                            </div>
                          </td>
                        );
                      })}
                    </tr>

                    {/* Average Progression */}
                    <tr>
                      <td className="px-3 py-2 text-sm font-medium text-gray-700">
                        <MetricLabel
                          label={t("statsNew.comparateur.metrics.avgProgression")}
                          tooltip={t("statsNew.comparateur.metricsTooltips.avgProgression")}
                        />
                      </td>
                      {selectedEntities.map((entity) => {
                        const { best, worst } = getBestWorst("averageProgression");
                        const progressionValue = entity.averageProgression ?? 0;
                        const hasProgression = entity.averageProgression !== null;
                        return (
                          <td
                            key={entity.id}
                            className={`px-3 py-2 text-center ${
                              entity.id === best
                                ? "bg-green-50"
                                : entity.id === worst
                                ? "bg-red-50"
                                : ""
                            }`}
                          >
                            <div className="flex flex-col items-center">
                              <span
                                className={`text-base font-semibold ${
                                  entity.id === best
                                    ? "text-green-600"
                                    : entity.id === worst
                                    ? "text-red-600"
                                    : "text-gray-900"
                                }`}
                              >
                                {hasProgression ? `${progressionValue}%` : "-"}
                              </span>
                              <div className="w-16 h-1.5 bg-gray-200 rounded-full mt-1 overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${
                                    entity.id === best
                                      ? "bg-green-500"
                                      : entity.id === worst
                                      ? "bg-red-500"
                                      : "bg-gray-400"
                                  }`}
                                  style={{ width: `${progressionValue}%` }}
                                />
                              </div>
                            </div>
                          </td>
                        );
                      })}
                    </tr>

                    {/* Average Score */}
                    <tr>
                      <td className="px-3 py-2 text-sm font-medium text-gray-700">
                        <MetricLabel
                          label={t("statsNew.comparateur.metrics.avgScore")}
                          tooltip={t("statsNew.comparateur.metricsTooltips.avgScore")}
                        />
                      </td>
                      {selectedEntities.map((entity) => {
                        const { best, worst } = getBestWorst("averageScore");
                        return (
                          <td
                            key={entity.id}
                            className={`px-3 py-2 text-center text-base font-semibold ${
                              entity.id === best
                                ? "text-green-600 bg-green-50"
                                : entity.id === worst
                                ? "text-red-600 bg-red-50"
                                : "text-gray-900"
                            }`}
                          >
                            {entity.averageScore !== null && entity.averageScoreMax !== null
                              ? `${entity.averageScore}/${entity.averageScoreMax} pts`
                              : "-"}
                          </td>
                        );
                      })}
                    </tr>

                    {/* Average Duration */}
                    <tr>
                      <td className="px-3 py-2 text-sm font-medium text-gray-700">
                        <MetricLabel
                          label={t("statsNew.comparateur.metrics.avgDuration")}
                          tooltip={t("statsNew.comparateur.metricsTooltips.avgDuration")}
                        />
                      </td>
                      {selectedEntities.map((entity) => (
                        <td
                          key={entity.id}
                          className="px-3 py-2 text-center text-base font-semibold text-gray-900"
                        >
                          {formatDuration(entity.averageDuration)}
                        </td>
                      ))}
                    </tr>

                    {/* Last Session Date */}
                    <tr>
                      <td className="px-3 py-2 text-sm font-medium text-gray-700">
                        <MetricLabel
                          label={t("statsNew.comparateur.metrics.lastSessionDate")}
                          tooltip={t("statsNew.comparateur.metricsTooltips.lastSessionDate")}
                        />
                      </td>
                      {selectedEntities.map((entity) => (
                        <td
                          key={entity.id}
                          className="px-3 py-2 text-center text-base font-semibold text-gray-900"
                        >
                          {formatLastSessionDate(entity.lastSessionDate)}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Charts - Activity Heatmaps */}
              {entitySessionsData.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    {t("statsNew.comparateur.charts.activityHeatmap")}
                  </h3>
                  <ActivityHeatmapComparison entities={entitySessionsData} />
                </div>
              )}

              {/* Charts - Sessions per month */}
              {entitySessionsData.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    {t("statsNew.comparateur.charts.sessionsPerMonth")}
                  </h3>
                  <div className="h-56">
                    <SessionsPerMonthComparisonChart
                      entities={entitySessionsData}
                      dateRange={dateRange}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Learner Group Modal */}
      <CreateLearnerGroupModal
        isOpen={showCreateGroupModal}
        onClose={() => setShowCreateGroupModal(false)}
        onCreateGroup={createGroup}
        onCreateSmartGroup={createSmartGroup}
        availableLearners={availableLearners}
        availableParcours={availableParcours}
        existingGroups={groups}
      />

      {/* Create Parcours Group Modal */}
      <CreateParcoursGroupModal
        isOpen={showCreateParcoursGroupModal}
        onClose={() => setShowCreateParcoursGroupModal(false)}
        onCreateGroup={createParcoursGroup}
        availableParcours={availableParcoursWithStats}
        existingGroups={parcoursGroups}
      />

      {/* Scene Selector Modal for Heatmap Comparison */}
      {showSceneSelector && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowSceneSelector(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t("statsNew.comparateur.heatmap.selectScene")}
                </h3>
                <button
                  onClick={() => setShowSceneSelector(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {t("statsNew.comparateur.heatmap.selectSceneDescription")}
              </p>
            </div>

            <div className="p-4 max-h-96 overflow-y-auto">
              {isLoadingScenes ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
                </div>
              ) : available360Scenes.length === 0 ? (
                <div className="text-center py-8">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 text-gray-300 mx-auto mb-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-gray-500">{t("statsNew.comparateur.heatmap.noScenes")}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {available360Scenes.map((scene) => (
                    <button
                      key={scene.id}
                      onClick={() => {
                        setSelectedSceneForComparison({ id: scene.id, name: scene.name });
                        setShowSceneSelector(false);
                        setShowHeatmapComparison(true);
                      }}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-green-500 hover:bg-green-50 transition-colors text-left"
                    >
                      <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                        {scene.sceneType === "video360" ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-gray-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"
                            />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-gray-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{scene.name}</p>
                        <p className="text-xs text-gray-500 truncate">{scene.parcoursName}</p>
                      </div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-gray-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Heatmap Comparison Modal */}
      {showHeatmapComparison && selectedSceneForComparison && (
        <GazeHeatmapModal
          sceneName={selectedSceneForComparison.name}
          contentId={selectedSceneForComparison.id}
          comparisonGroups={groups.map((g, index) => ({
            ...g,
            color: getEntityColor(index).primary,
          }))}
          onClose={() => {
            setShowHeatmapComparison(false);
            setSelectedSceneForComparison(null);
          }}
        />
      )}
    </div>
  );
};

export default ComparateurView;
